/**
 * sentry.ts — production-only crash reporting (BETA 20.96).
 *
 * Routes uncaught errors + unhandled promise rejections from REAL players on
 * theburntpeanutsimulator.com (the live site) to Dylan's Sentry project so we
 * get pinged the moment a live crash happens, bucketed by BETA version.
 *
 * Deliberately CRASH-CAPTURE ONLY:
 *   - NO performance tracing (tracesSampleRate 0) — can't cost FPS in the rAF loop.
 *   - NO session replay — a full-screen <canvas> game gets nothing useful from
 *     DOM replay and it would burn the free Sentry quota.
 *   - NO console-log forwarding — the bot/dev logs would flood the quota.
 *   - Production builds ONLY — never fires during `vite dev`, the headless smoke
 *     test, or local bot runs, so Dylan's inbox + quota stay clean.
 *   - Privacy-preserving (sendDefaultPii: false) — players are anonymous.
 *   - Fully guarded: a Sentry failure can never break the game.
 *
 * Coexists with perfTelemetry.ts's own error handlers — both use
 * addEventListener (no window.onerror clobbering).
 */
import * as Sentry from '@sentry/react';

// The DSN is a PUBLIC client key — safe to embed. It can only WRITE new events,
// never read data, and ships inside every Sentry-instrumented website. Hardcoded
// as the default so the Vercel build works with zero env setup (.env is
// gitignored, so Vercel wouldn't otherwise see it); an optional VITE_SENTRY_DSN
// env var overrides it if we ever want a separate project.
const DEFAULT_DSN =
  'https://93a5feb7464a53c0f4d8595146da4c30@o4511565434060800.ingest.us.sentry.io/4511565892747264';
const DSN = (import.meta.env.VITE_SENTRY_DSN as string | undefined) || DEFAULT_DSN;

export function initSentry(): void {
  // Production only. import.meta.env.PROD is false under `vite dev` + the smoke
  // test, so no crash reports flow while developing or bot-testing.
  if (!import.meta.env.PROD) return;
  if (!DSN) return;
  try {
    Sentry.init({
      dsn: DSN,
      environment: import.meta.env.MODE, // 'production' on the live site
      sendDefaultPii: false, // anonymous players — no IP / user data attached
      tracesSampleRate: 0, // crash capture only — no performance tracing
      ignoreErrors: [
        // Browser/extension noise that isn't our bug.
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        /^Non-Error promise rejection captured/,
      ],
    });
    // Tag every crash with the BETA version so issues bucket per ship.
    // version.json is already served + bumped every release → no extra marker
    // to keep in sync. Best-effort: a crash in the first ~50ms before this
    // resolves is still captured, just without the tag.
    fetch('/version.json')
      .then((r) => r.json())
      .then((v: { version?: string }) => {
        if (v?.version) Sentry.setTag('beta', v.version);
      })
      .catch(() => {
        /* version tag is best-effort */
      });
  } catch {
    /* Sentry must never break the game — swallow any init failure. */
  }
}
